import { useState, useEffect } from 'react';
import AppLayout from '../layout/AppLayout';
import { Spinner } from 'react-bootstrap';
import regenerateToken from '../regenerateToken';
import Loader from '../loader/loader';
import axios from 'axios';
import Cookies from 'js-cookie';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';
import { useAccessConfig } from '../layout/AccessContext';
import { useAdmin } from '../layout/AdminContext';
import AccessRestricted from '../layout/AccessRestricted';
import { FaPalette, FaFont, FaMobileAlt, FaSave, FaCheck } from 'react-icons/fa';
import './vizappConfiguration.css';

const VizappConfiguration = () => {
  const { accessConfig, loading: accessLoading } = useAccessConfig();
  const { isAdmin } = useAdmin();

  // hooks for loaders
  const [loading, setLoading] = useState(false);
  const [updatingLoader, setUpdatingLoader] = useState(false);
  const [saveOption, setSaveOption] = useState(false);
  const [vizappConfiguration, setVizappConfiguration] = useState({
    primaryColor: '#3567d6',
    secondaryColor: '#E8F1FA',
    introductionType: '',
    introGradient: false,
    fontFamily: '',
    backgroundType: '',
  });

  const getVizappConfiguration = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: 'GET',
        url: 'https://api.niroggyan.com/onboarding/config/vizapp',
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get('idToken');
    return await axios({
      headers: { authorization: idToken },
      method: 'GET',
      url: 'https://api.niroggyan.com/onboarding/config/vizapp',
      validateStatus: (s) => s >= 200 && s <= 401,
    });
  };

  useEffect(() => {
    // Only fetch if user has access (admins always have access)
    if (!accessLoading && (isAdmin || accessConfig.vizappConfiguration)) {
      (async function () {
        setLoading(true);
        try {
          const existingStateConfigResponse = await getVizappConfiguration();
          const statusCode = existingStateConfigResponse.status;
          if (statusCode === 401) {
            console.log('Regenerating ID Token');
            await regenerateToken();
            const retryResponse = await getVizappConfiguration();

            if (retryResponse.status === 401) {
              console.error('Authentication failed after token regeneration');
              localStorage.removeItem('credentials');
              Cookies.remove('idToken');
              Cookies.remove('refreshToken');
              Cookies.remove('accessToken');
              window.location.href = '/login';
              return;
            }

            if (retryResponse?.data?.data) {
              setVizappConfiguration(retryResponse.data.data);
            }
            setLoading(false);
          } else {
            if (existingStateConfigResponse?.data?.data) {
              setVizappConfiguration(existingStateConfigResponse.data.data);
            }
            setLoading(false);
          }
        } catch (error) {
          console.log('error', error.message);
          setLoading(false);
        }
      })();
    }
  }, [accessLoading, accessConfig.vizappConfiguration, isAdmin]);

  // Access control check - show restricted page if no access (after all hooks)
  // Admins are never restricted
  if (!isAdmin && !accessLoading && !accessConfig.vizappConfiguration) {
    return <AccessRestricted featureName="Vizapp Configuration" />;
  }

  const updatingVizappStateFields = (updatedValue, type) => {
    const getBooleanValue = () => {
      return updatedValue === 'true' ? true : false;
    };

    setVizappConfiguration(prevConfig => ({
      ...prevConfig,
      [type]: ['true', 'false'].includes(updatedValue)
        ? getBooleanValue()
        : updatedValue,
    }));
  };

  const updatingVizappConfiguration = async () => {
    // Deleting the Unwanted keys
    const body = JSON.parse(JSON.stringify(vizappConfiguration));
    delete body['clientId'];
    delete body['createdAt'];
    delete body['updatedAt'];

    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: 'POST',
        url: 'https://api.niroggyan.com/onboarding/config/vizapp',
        data: body,
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get('idToken');
    return await axios({
      headers: { authorization: idToken },
      method: 'POST',
      url: 'https://api.niroggyan.com/onboarding/config/vizapp',
      data: JSON.stringify(body),
      validateStatus: (s) => s >= 200 && s <= 401,
    });
  };

  const handlingRequest = async () => {
    // API call for Updating Vizapp Configuration
    setUpdatingLoader(true);
    try {
      const VizappConfigurationResponse = await updatingVizappConfiguration();
      if (VizappConfigurationResponse.status === 401) {
        console.log('Regenerating ID Token');
        await regenerateToken();
        const VizappConfigurationResponse = await updatingVizappConfiguration();
        setUpdatingLoader(false);
        console.log(
          VizappConfigurationResponse.data.data.acknowledged
            ? 'Successfully Updated the Vizapp configuration @ first trail'
            : 'Not Updated Successfully'
        );
      } else {
        console.log('VizappConfigurationResponse', VizappConfigurationResponse);
        setUpdatingLoader(false);
        console.log(
          VizappConfigurationResponse.data.data.acknowledged
            ? `Successfully Updated the Vizapp configuration @ first trail`
            : 'Not Updated Successfully'
        );
      }
    } catch (e) {
      setUpdatingLoader(false);
      console.log('Error in updating Vizapp Configuration', e.message);
    }
  };

  return (
    <AppLayout>
      {loading ? (
        <Loader />
      ) : (
        <div className="vizapp-config-container">
          <div className="vizapp-header">
            <h1>VizApp Configuration</h1>
            <p>Customize the visual appearance of your mobile application experience.</p>
          </div>

          <div className="row g-4">
            {/* Color Theme Card */}
            <div className="col-12 col-md-6">
              <div className="config-card">
                <div className="card-title">
                  <div className="section-icon">
                    <FaPalette size={20} />
                  </div>
                  Color Theme
                </div>

                <div className="form-group">
                  <label className="form-label">Primary Color</label>
                  <div className="color-picker-wrapper">
                    <div
                      className="color-preview"
                      style={{ backgroundColor: vizappConfiguration?.primaryColor || '#3567d6' }}
                    ></div>
                    <input
                      type="color"
                      className="color-input"
                      value={vizappConfiguration?.primaryColor || '#3567d6'}
                      onChange={(e) => {
                        updatingVizappStateFields(e.target.value, 'primaryColor');
                        setSaveOption(true);
                      }}
                    />
                    <span className="color-value">{vizappConfiguration?.primaryColor || '#3567d6'}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Secondary Color</label>
                  <div className="color-picker-wrapper">
                    <div
                      className="color-preview"
                      style={{ backgroundColor: vizappConfiguration?.secondaryColor || '#E8F1FA' }}
                    ></div>
                    <input
                      type="color"
                      className="color-input"
                      value={vizappConfiguration?.secondaryColor || '#E8F1FA'}
                      onChange={(e) => {
                        updatingVizappStateFields(e.target.value, 'secondaryColor');
                        setSaveOption(true);
                      }}
                    />
                    <span className="color-value">{vizappConfiguration?.secondaryColor || '#E8F1FA'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Design & Typography Card */}
            <div className="col-12 col-md-6">
              <div className="config-card h-100">
                <div className="card-title">
                  <div className="section-icon">
                    <FaFont size={20} />
                  </div>
                  Design & Typography
                </div>

                <div className="form-group">
                  <label className="form-label">Font Family</label>
                  <select
                    className="custom-select"
                    value={vizappConfiguration.fontFamily}
                    onChange={(e) => {
                      updatingVizappStateFields(e.target.value, 'fontFamily');
                      setSaveOption(true);
                    }}
                  >
                    <option hidden>Select Font Family</option>
                    <option value={'Roboto'}>Roboto</option>
                    <option value={'Open Sans'}>Open Sans</option>
                    <option value={'Comfortaa'}>Comfortaa</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Background Style</label>
                  <select
                    className="custom-select"
                    value={vizappConfiguration.backgroundType}
                    onChange={(e) => {
                      updatingVizappStateFields(e.target.value, 'backgroundType');
                      setSaveOption(true);
                    }}
                  >
                    <option hidden>Select Background Type</option>
                    <option value={'Classic'}>Classic</option>
                    <option value={'Modern'}>Modern</option>
                    <option value={'Solid'}>Solid</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Introduction Settings Card */}
            <div className="col-12">
              <div className="config-card">
                <div className="card-title">
                  <div className="section-icon">
                    <FaMobileAlt size={20} />
                  </div>
                  Introduction Screen
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">Introduction Type</label>
                      <select
                        className="custom-select"
                        value={vizappConfiguration.introductionType}
                        onChange={(e) => {
                          updatingVizappStateFields(e.target.value, 'introductionType');
                          setSaveOption(true);
                        }}
                      >
                        <option hidden>Select Introduction Type</option>
                        <option value={'Classic'}>Classic</option>
                        <option value={'Modern'}>Modern</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">Intro Gradient Overlay</label>
                      <select
                        className="custom-select"
                        value={vizappConfiguration.introGradient}
                        onChange={(e) => {
                          updatingVizappStateFields(e.target.value, 'introGradient');
                          setSaveOption(true);
                        }}
                      >
                        <option hidden>Select Option</option>
                        <option value={true}>Enabled</option>
                        <option value={false}>Disabled</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="action-bar">
            <button
              onClick={() => setSaveOption(false)}
              className="btn-vizapp btn-save"
              disabled={!saveOption}
            >
              <FaSave /> Save Draft
            </button>
            <button
              onClick={() => handlingRequest()}
              disabled={saveOption}
              className="btn-vizapp btn-confirm"
              style={{ minWidth: '160px' }}
            >
              {updatingLoader ? (
                <>
                  <Spinner animation="border" size="sm" as="span" role="status" aria-hidden="true" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <FaCheck /> Confirm Changes
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default VizappConfiguration;
